import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, teamMembers } from '@/mocks/articles';
import { useState } from 'react';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于明智育儿指南',
    description: siteInfo.description,
    url: 'https://aitdev.cn/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '明智育儿指南',
    url: 'https://aitdev.cn',
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    foundingDate: String(siteInfo.foundingYear),
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座1508室',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    foundingLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: '北京',
        addressCountry: 'CN',
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    setFormStatus('submitting');
    setFormError('');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u3nmt3pcjqs2fcsle0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.meta as Record<string, string>)?.detail || responseText || '提交失败，请稍后再试';
        setFormStatus('error');
        setFormError(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 明智育儿指南 | 品牌介绍与联系方式"
        description={`明智育儿指南成立于${siteInfo.foundingYear}年，${siteInfo.description}了解我们的专家团队和联系方式。`}
        keywords="明智育儿指南,关于我们,儿童心理专家,家庭教育咨询,联系地址"
        canonicalUrl="https://aitdev.cn/about"
        schemaJson={[aboutSchema, orgSchema]}
      />

      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            <div className="pb-10">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                关于我们
              </h1>
              <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
                了解明智育儿指南的品牌故事、专家团队与联系信息
              </p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              品牌故事
            </h2>
            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                明智育儿指南创立于{siteInfo.foundingYear}年，由一群关注儿童心理健康的心理学研究者、教育工作者和临床咨询师共同发起。我们的使命是在科学证据与家庭实践之间架起一座桥梁，用通俗易懂的方式向家长传递前沿的儿童发展研究成果。
              </p>
              <p>
                我们深知，每一位家长都希望给孩子最好的教育，但在实际育儿过程中，面对孩子的"不听话"、情绪爆发和挑战行为时，常常感到困惑、无助甚至愤怒。特别是在中国文化背景下，"不打不成器"的传统观念与新兴的科学育儿理念之间的冲突，让许多家长陷入了两难境地。
              </p>
              <p>
                正是基于这样的观察，我们创建了明智育儿指南这一平台。我们不评判、不指责，而是用科学、客观、温暖的视角，帮助家长了解"打屁股"背后的心理学真相，掌握正面管教的实用方法，在尊重孩子的同时坚守教育的边界。
              </p>
              <p>
                到目前为止，明智育儿指南已经帮助超过<strong>50万</strong>个中国家庭改善了亲子关系，我们的文章和课程被上百所学校和幼儿园作为家长教育的参考材料。但我们知道，这只是一个开始——让每一个孩子都能在尊重和安全的环境中成长，是我们持续努力的方向。
              </p>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                专家团队
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                由心理学博士、教育学家和资深咨询师组成的专业团队
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden text-center"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={member.imageUrl}
                      alt={member.name}
                      title={`${member.name} - ${member.title}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-semibold text-foreground-950 mb-1">{member.name}</h3>
                    <p className="text-xs text-accent-600 font-medium mb-3 whitespace-nowrap">{member.title}</p>
                    <p className="text-sm text-foreground-600 leading-relaxed">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <p className="text-sm text-foreground-600">
                有任何问题或建议？欢迎联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              <div className="flex flex-col items-center text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h4>
                <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">
                  {siteInfo.email}
                </a>
              </div>
              <div className="flex flex-col items-center text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1">咨询电话</h4>
                <a href={`tel:${siteInfo.phone}`} className="text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap">
                  {siteInfo.phone}
                </a>
              </div>
              <div className="flex flex-col items-center text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-950 mb-1">办公地址</h4>
                <p className="text-sm text-foreground-600">{siteInfo.address}</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
              <h3 className="text-lg font-semibold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                发送消息
              </h3>

              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h4 className="text-base font-semibold text-foreground-950 mb-2">消息已发送</h4>
                  <p className="text-sm text-foreground-600">感谢您的留言，我们会在1-2个工作日内回复您。</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" id="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-sm text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="消息主题"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">消息内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的问题或建议（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="contact-form-hp"
                  />
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 mb-4">{formError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="inline-flex items-center px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                    <i className="ri-send-plane-line ml-2"></i>
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-14">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              我们的位置
            </h2>
            <div className="rounded-lg overflow-hidden border border-background-200/70">
              <iframe
                title="明智育儿指南办公地址"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3061.640943687595!2d116.465342677442!3d39.908695671595!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f0521e6c5c5f2b%3A0x5e2a5e3b3e2d5693!2sSOHO%20Modern%20City!5e0!3m2!1sen!2scn!4v1692000000000!5m2!1sen!2scn"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                aria-label="明智育儿指南办公地点地图"
              ></iframe>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}